import React from 'react';
import { Link, Banner } from '@cad-arc';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import {
    DispatchAreaObject,
    DispatchAreasResource,
    ManageDispatchAreasResource,
} from '@mark43/cad-api';

function UnownedAreaLink(props: {
    dispatchAreaObject: DispatchAreaObject;
    takeDispatchArea: (dispatchAreaId: number) => void;
}): React.ReactElement {
    return (
        <Link
            onClick={() => {
                props.takeDispatchArea(props.dispatchAreaObject.id);
            }}
            style={{ textDecoration: 'underline' }}
        >
            Dispatch {props.dispatchAreaObject.name}
        </Link>
    );
}

export function UnownedDispatchAreasBanner(props: { pollingInterval?: number }) {
    const apiClient = useApiClient();
    const { data: unownedDispatchAreasView, refetch } = useQuery(['unowned-dispatch-areas'], () => {
        return apiClient
            .request<ManageDispatchAreasResource.GetUnownedDispatchAreasView>({
                method: 'GET',
                url: 'manage_dispatch_areas/unowned',
            })
            .then((resp) => resp.data.data);
    });
    React.useEffect(() => {
        const intervalID = setInterval(() => {
            refetch();
        }, props.pollingInterval ?? 20000);
        return () => {
            clearInterval(intervalID);
        };
    }, [props.pollingInterval, refetch]);

    const takeDispatchArea = React.useCallback(
        (dispatchAreaId: number) => {
            apiClient
                .request<DispatchAreasResource.TakeDispatchAreasForUser>({
                    data: [dispatchAreaId],
                    method: 'POST',
                    url: 'dispatch_areas/ownership/links/take',
                })
                .then(() => {
                    refetch();
                });
        },
        [apiClient, refetch]
    );

    if (!unownedDispatchAreasView || unownedDispatchAreasView.dispatchAreaObjects.length === 0) {
        return null;
    }

    const unownedDispatchAreas = unownedDispatchAreasView.dispatchAreaObjects;
    return (
        <Banner
            style={{ marginTop: 0, display: 'flex', justifyContent: 'center' }}
            status="error"
            title="Warning:"
            description={
                unownedDispatchAreas.length === 1 ? (
                    <span>
                        One D-area has no coverage.{' '}
                        <UnownedAreaLink
                            dispatchAreaObject={unownedDispatchAreas[0]}
                            takeDispatchArea={takeDispatchArea}
                        />
                    </span>
                ) : (
                    <span>
                        Some D-areas do not have coverage.{' '}
                        {unownedDispatchAreas.map((dispatchAreaObject, i) => (
                            <React.Fragment key={dispatchAreaObject.id}>
                                <UnownedAreaLink
                                    dispatchAreaObject={dispatchAreaObject}
                                    takeDispatchArea={takeDispatchArea}
                                />
                                {i !== unownedDispatchAreas.length - 1 && ', '}
                            </React.Fragment>
                        ))}
                    </span>
                )
            }
        />
    );
}
